<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { usePagination } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import { AdminHostingListResponse, type AdminHostingSiteRow } from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import { formatBytes } from '@/utils'

defineOptions({ name: 'AdminHostingList' })

const { t } = useI18n()
const router = useRouter()

const loading = ref(true)
const sites = ref<AdminHostingSiteRow[]>([])
const statusFilter = ref<string>('all')
const legacyFilter = ref<string>('all') // all | migrated | fresh
const searchQuery = ref('')
const searchDebounce = ref<ReturnType<typeof setTimeout> | null>(null)

const pagination = usePagination({ initialItemsPerPage: 50 })

const statusOptions = computed(() => [
  { value: 'all', title: t('dashboard.admin.hosting.status.all') },
  { value: 'active', title: t('dashboard.admin.hosting.status.active') },
  { value: 'provisioning', title: t('dashboard.admin.hosting.status.provisioning') },
  { value: 'failed', title: t('dashboard.admin.hosting.status.failed') },
  { value: 'suspended', title: t('dashboard.admin.hosting.status.suspended') },
  { value: 'deprovisioned', title: t('dashboard.admin.hosting.status.deprovisioned') }
])

const legacyOptions = computed(() => [
  { value: 'all', title: t('dashboard.admin.hosting.legacy.all') },
  { value: 'migrated', title: t('dashboard.admin.hosting.legacy.migrated') },
  { value: 'fresh', title: t('dashboard.admin.hosting.legacy.fresh') }
])

async function fetchSites() {
  loading.value = true
  const { limit, offset } = pagination.buildQueryParams()

  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
  if (statusFilter.value !== 'all') params.set('status', statusFilter.value)
  if (legacyFilter.value === 'migrated') params.set('has_legacy_vhost', 'true')
  if (legacyFilter.value === 'fresh') params.set('has_legacy_vhost', 'false')
  if (searchQuery.value.trim()) params.set('search', searchQuery.value.trim())

  try {
    const res = await useAxios<null, unknown>('GET', `/admin/hosting/sites?${params.toString()}`, true)
    if (res && res.status === STATUS_CODES.OK) {
      const parsed = AdminHostingListResponse.safeParse(res.data)
      if (parsed.success) {
        sites.value = parsed.data.data
        pagination.setTotal(parsed.data.total)
      } else {
        console.error('[AdminHostingList] parse failed', parsed.error.issues)
      }
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminHostingList' })
  } finally {
    loading.value = false
  }
}

watch(() => pagination.page.value, fetchSites)

watch([statusFilter, legacyFilter], () => {
  pagination.goToPage(1)
  fetchSites()
})

watch(searchQuery, () => {
  if (searchDebounce.value) clearTimeout(searchDebounce.value)
  searchDebounce.value = setTimeout(() => {
    pagination.goToPage(1)
    fetchSites()
  }, 300)
})

const showingFrom = computed(() => ((pagination.page.value - 1) * pagination.itemsPerPage.value) + 1)
const showingTo = computed(() => Math.min(showingFrom.value + sites.value.length - 1, pagination.total.value))

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-'
  return DateTime.fromISO(iso).toFormat('dd.MM.yyyy')
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'active': return 'status-active'
    case 'provisioning': return 'status-provisioning'
    case 'failed': return 'status-failed'
    case 'suspended': return 'status-suspended'
    case 'deprovisioned': return 'status-deprovisioned'
    default: return ''
  }
}

function getStatusLabel(status: string): string {
  const opt = statusOptions.value.find(s => s.value === status)
  return opt?.title || status
}

function usagePercent(row: AdminHostingSiteRow): number {
  if (row.quota_bytes === 0) return 0
  return Math.round((row.bytes_used / row.quota_bytes) * 100)
}

function navigateToDetail(id: number) {
  router.push({ name: 'dashboardAdminHostingDetail', params: { id } })
}

onMounted(fetchSites)
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.admin.hosting.title')"
    :subtitle="t('dashboard.admin.hosting.subtitle')"
    icon="mdi-web-box"
    icon-color="cyan"
  >
    <div class="filters-bar">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        :placeholder="t('dashboard.admin.hosting.searchPlaceholder')"
        variant="outlined"
        density="compact"
        hide-details
        class="search-input"
      />
      <div class="status-filters">
        <button
          v-for="opt in statusOptions"
          :key="opt.value"
          class="filter-chip"
          :class="{ active: statusFilter === opt.value }"
          @click="statusFilter = opt.value"
        >
          {{ opt.title }}
        </button>
      </div>
      <div class="status-filters">
        <button
          v-for="opt in legacyOptions"
          :key="opt.value"
          class="filter-chip"
          :class="{ active: legacyFilter === opt.value }"
          @click="legacyFilter = opt.value"
        >
          {{ opt.title }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="state-box">
      <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
    </div>

    <div v-else-if="sites.length === 0" class="state-box">
      <v-icon icon="mdi-web-box" size="48" class="empty-icon" />
      <p class="empty-title">{{ t('dashboard.admin.hosting.empty.title') }}</p>
      <p class="empty-hint">{{ t('dashboard.admin.hosting.empty.description') }}</p>
    </div>

    <div v-else class="section-card">
      <div class="data-table">
        <div class="table-header">
          <span class="col">{{ t('dashboard.admin.hosting.table.name') }}</span>
          <span class="col">{{ t('dashboard.admin.hosting.table.status') }}</span>
          <span class="col">{{ t('dashboard.admin.hosting.table.account') }}</span>
          <span class="col">{{ t('dashboard.admin.hosting.table.product') }}</span>
          <span class="col">{{ t('dashboard.admin.hosting.table.quota') }}</span>
          <span class="col">{{ t('dashboard.admin.hosting.table.domain') }}</span>
          <span class="col">{{ t('dashboard.admin.hosting.table.created') }}</span>
          <span class="col"></span>
        </div>
        <div
          v-for="row in sites"
          :key="row.id"
          class="table-row clickable"
          @click="navigateToDetail(row.id)"
        >
          <span class="col">
            <span class="mono">{{ row.name }}</span>
            <span v-if="row.legacy_vhost_id" class="legacy-badge">legacy</span>
          </span>
          <span class="col">
            <span class="status-chip" :class="getStatusClass(row.status)">
              {{ getStatusLabel(row.status) }}
            </span>
          </span>
          <span class="col account-cell">
            <span class="account-email">{{ row.account_email }}</span>
            <span class="account-id">#{{ row.account_id }}</span>
          </span>
          <span class="col product-cell">{{ row.product_slug }}</span>
          <span class="col quota-cell">
            {{ formatBytes(row.bytes_used) }} / {{ formatBytes(row.quota_bytes) }}
            <span class="quota-pct">({{ usagePercent(row) }}%)</span>
          </span>
          <span class="col domain-cell">{{ row.primary_domain || '-' }}</span>
          <span class="col date-cell">{{ formatDate(row.created_at) }}</span>
          <span class="col action-col">
            <v-icon icon="mdi-chevron-right" size="18" />
          </span>
        </div>
      </div>
    </div>

    <div v-if="pagination.total.value > 0 && !loading" class="pagination-controls">
      <div class="pagination-info">
        {{ t('dashboard.admin.hosting.pagination.showing', { from: showingFrom, to: showingTo, total: pagination.total.value }) }}
      </div>
      <div class="pagination-buttons">
        <button class="pagination-btn" :disabled="pagination.page.value === 1" @click="pagination.goToPage(1)">
          <v-icon icon="mdi-chevron-double-left" size="18" />
        </button>
        <button class="pagination-btn" :disabled="pagination.page.value === 1" @click="pagination.prevPage()">
          <v-icon icon="mdi-chevron-left" size="18" />
        </button>
        <span class="page-indicator">{{ pagination.page.value }} / {{ pagination.totalPages.value }}</span>
        <button class="pagination-btn" :disabled="pagination.page.value === pagination.totalPages.value" @click="pagination.nextPage()">
          <v-icon icon="mdi-chevron-right" size="18" />
        </button>
        <button class="pagination-btn" :disabled="pagination.page.value === pagination.totalPages.value" @click="pagination.goToPage(pagination.totalPages.value)">
          <v-icon icon="mdi-chevron-double-right" size="18" />
        </button>
      </div>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.filters-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.search-input { max-width: 280px; flex: 1; }
.status-filters { display: flex; gap: 6px; flex-wrap: wrap; }
.filter-chip {
  padding: 6px 12px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { background: rgba(120, 163, 255, 0.15); }
  &.active {
    background: rgba(0, 176, 255, 0.15);
    border-color: rgba(0, 176, 255, 0.3);
    color: #00B0FF;
  }
}
.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  overflow: hidden;
}
.data-table { overflow-x: auto; }
.table-header, .table-row {
  display: grid;
  grid-template-columns: 1.4fr 0.9fr 1.6fr 0.9fr 1.4fr 1.4fr 0.9fr 40px;
  gap: 12px;
  align-items: center;
  padding: 10px 16px;
  min-width: 1000px;
}
.table-header {
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  transition: background 0.15s ease;
  &:last-child { border-bottom: none; }
  &:hover { background: rgba(120, 163, 255, 0.04); }
  &.clickable { cursor: pointer; }
}
.mono { font-family: 'JetBrains Mono', 'Fira Code', monospace; font-size: 0.85rem; font-weight: 500; }
.legacy-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(186, 104, 200, 0.15);
  border: 1px solid rgba(186, 104, 200, 0.3);
  color: #ba68c8;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
}
.status-chip { display: inline-block; padding: 2px 8px; border-radius: 6px; font-size: 0.78rem; font-weight: 500; }
.status-active { background: rgba(129, 199, 132, 0.1); border: 1px solid rgba(129, 199, 132, 0.3); color: #81c784; }
.status-provisioning { background: rgba(255, 183, 77, 0.1); border: 1px solid rgba(255, 183, 77, 0.3); color: #ffb74d; }
.status-failed { background: rgba(244, 67, 54, 0.1); border: 1px solid rgba(244, 67, 54, 0.3); color: #ef5350; }
.status-suspended { background: rgba(255, 152, 0, 0.1); border: 1px solid rgba(255, 152, 0, 0.3); color: #ff9800; }
.status-deprovisioned { background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); color: rgba(200, 218, 255, 0.5); }
.account-cell { display: flex; flex-direction: column; gap: 2px; }
.account-email { font-size: 0.85rem; color: rgba(255, 255, 255, 0.8); }
.account-id { font-size: 0.75rem; color: rgba(200, 218, 255, 0.5); }
.product-cell, .quota-cell, .domain-cell { font-size: 0.85rem; color: rgba(200, 218, 255, 0.7); }
.quota-pct { color: rgba(200, 218, 255, 0.5); margin-left: 4px; font-size: 0.75rem; }
.date-cell { font-size: 0.85rem; color: rgba(200, 218, 255, 0.6); white-space: nowrap; }
.action-col { text-align: right; color: rgba(200, 218, 255, 0.4); }
.state-box { display: flex; flex-direction: column; align-items: center; padding: 48px 24px; text-align: center; }
.empty-icon { color: rgba(200, 218, 255, 0.25); margin-bottom: 12px; }
.empty-title { font-size: 1rem; font-weight: 500; color: rgba(200, 218, 255, 0.6); margin: 0 0 4px; }
.empty-hint { font-size: 0.85rem; color: rgba(200, 218, 255, 0.4); margin: 0; }
.pagination-controls {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; margin-top: 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 12px;
}
.pagination-info { font-size: 0.85rem; color: rgba(200, 218, 255, 0.6); }
.pagination-buttons { display: flex; align-items: center; gap: 8px; }
.pagination-btn {
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 36px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover:not(:disabled) { background: rgba(0, 176, 255, 0.1); border-color: rgba(0, 176, 255, 0.25); color: #00B0FF; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}
.page-indicator {
  font-size: 0.9rem; font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  padding: 0 12px; min-width: 80px; text-align: center;
}
@media (max-width: 768px) {
  .filters-bar { flex-direction: column; align-items: stretch; }
  .search-input { max-width: 100%; }
  .pagination-controls { flex-direction: column; gap: 12px; }
}
</style>
